/**
 * RBACAuditLogPanel - RBAC Audit Log Viewer with Timeline & Export
 *
 * FERP CRITICAL: Complete audit trail for all RBAC operations
 *
 * Features:
 * - View all RBAC operations (role CRUD, assignments, revocations)
 * - Filter by operation type, user, date range, outcome
 * - Timeline visualization of audit events
 * - Role change tracking with statistics
 * - Export audit log (CSV/JSON/PDF) for compliance
 * - Real-time updates via polling
 *
 * Architecture Principles:
 * - Data-driven (all audit entries from RBACAuditRepository)
 * - Immutable audit trail
 * - Repository pattern for operations
 * - Life-critical compliance logging
 * - Multi-tenant isolation
 *
 * GitHub Issue: #339
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Table,
  Group,
  Text,
  Badge,
  Button,
  TextInput,
  Select,
  Alert,
  Stack,
  ActionIcon,
  Tooltip,
  Box,
  Code,
  Card,
  Timeline,
  Title,
  Grid,
  Paper,
  Loader,
  Center,
  Modal,
  Divider,
  ScrollArea,
  RingProgress,
} from '@mantine/core';
import {
  IconClipboardList,
  IconEye,
  IconSearch,
  IconAlertCircle,
  IconDownload,
  IconRefresh,
  IconFilter,
  IconClock,
  IconCheck,
  IconX,
  IconChartBar,
  IconFileDownload,
  IconFileTypeCsv,
  IconFileTypePdf,
  IconFile,
} from '@tabler/icons-react';
import { useApolloClient } from '@apollo/client/react';
import { ApolloClient } from '@apollo/client';
import {
  RBACAuditRepository,
  createRBACAuditRepository,
  RBACAuditLogEntry,
  RBACAuditFilter,
  RBACAuditStatistics,
  ExportRBACAuditInput,
} from '../data/repositories/RBACAuditRepository';
import { DatePickerInput } from '@mantine/dates';

export interface RBACAuditLogPanelProps {
  /** Tenant ID for multi-tenant isolation */
  tenantId: string;
  /** Enable automatic polling for real-time updates */
  autoRefresh?: boolean;
  /** Polling interval in milliseconds (default: 5000ms) */
  pollingInterval?: number;
  /** Is loading */
  isLoading: boolean;
}

/**
 * RBAC Audit Log Panel
 *
 * FERP Architecture Compliance:
 * - ✅ Data-driven (audit entries from RBACAuditRepository)
 * - ✅ Backend Snowflake ID generation
 * - ✅ Repository pattern
 * - ✅ Immutable audit trail
 * - ✅ Complete compliance logging
 * - ✅ Multi-tenant isolation
 */
export function RBACAuditLogPanel({
  tenantId,
  autoRefresh = false,
  pollingInterval = 5000,
  isLoading: _parentLoading,
}: RBACAuditLogPanelProps) {
  const apolloClient = useApolloClient() as ApolloClient;
  const repository = useMemo(
    () => createRBACAuditRepository(apolloClient),
    [apolloClient]
  );

  // State
  const [auditLog, setAuditLog] = useState<RBACAuditLogEntry[]>([]);
  const [statistics, setStatistics] = useState<RBACAuditStatistics | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOperation, setFilterOperation] = useState<string | null>(null);
  const [filterOutcome, setFilterOutcome] = useState<string | null>(null);
  const [filterEntityType, setFilterEntityType] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null] | [string | null, string | null]>([null, null]);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // View modes
  const [viewMode, setViewMode] = useState<'table' | 'timeline'>('table');
  const [showStatistics, setShowStatistics] = useState(false);

  // Detail modal
  const [selectedEntry, setSelectedEntry] = useState<RBACAuditLogEntry | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Export
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<'CSV' | 'JSON' | 'PDF'>('CSV');

  /**
   * Load audit log from backend
   * FERP: Repository pattern for all data access
   */
  const loadAuditLog = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filter: RBACAuditFilter = {
        tenantId,
        operation: filterOperation || undefined,
        outcome: filterOutcome || undefined,
        entityType: filterEntityType || undefined,
        startDate: dateRange[0] ? (dateRange[0] instanceof Date ? dateRange[0].toISOString() : dateRange[0]) : undefined,
        endDate: dateRange[1] ? (dateRange[1] instanceof Date ? dateRange[1].toISOString() : dateRange[1]) : undefined,
        searchQuery: searchQuery || undefined,
        page,
        pageSize,
      };

      const response = await repository.listAuditLog(filter);
      setAuditLog(response.entries);
      setTotalCount(response.totalCount);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [
    repository,
    tenantId,
    filterOperation,
    filterOutcome,
    filterEntityType,
    dateRange,
    searchQuery,
    page,
    pageSize,
  ]);

  /**
   * Load audit statistics
   * FERP: Analytics from backend repository
   */
  const loadStatistics = useCallback(async () => {
    try {
      const stats = await repository.getStatistics(
        tenantId,
        dateRange[0] ? (dateRange[0] instanceof Date ? dateRange[0].toISOString() : dateRange[0]) : undefined,
        dateRange[1] ? (dateRange[1] instanceof Date ? dateRange[1].toISOString() : dateRange[1]) : undefined
      );
      setStatistics(stats);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  }, [repository, tenantId, dateRange]);

  // Load audit log on mount and filter changes
  useEffect(() => {
    loadAuditLog();
  }, [loadAuditLog]);

  // Load statistics when statistics panel is shown
  useEffect(() => {
    if (showStatistics) {
      loadStatistics();
    }
  }, [showStatistics, loadStatistics]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return undefined;

    const interval = setInterval(() => {
      loadAuditLog();
      if (showStatistics) {
        loadStatistics();
      }
    }, pollingInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, pollingInterval, loadAuditLog, loadStatistics, showStatistics]);

  /**
   * Handle export
   * FERP: Export to CSV/JSON/PDF with download
   */
  const handleExport = async () => {
    setExporting(true);
    try {
      const filter: RBACAuditFilter = {
        tenantId,
        operation: filterOperation || undefined,
        outcome: filterOutcome || undefined,
        entityType: filterEntityType || undefined,
        startDate: dateRange[0] ? (dateRange[0] instanceof Date ? dateRange[0].toISOString() : dateRange[0]) : undefined,
        endDate: dateRange[1] ? (dateRange[1] instanceof Date ? dateRange[1].toISOString() : dateRange[1]) : undefined,
        searchQuery: searchQuery || undefined,
      };

      const input: ExportRBACAuditInput = {
        filter,
        format: exportFormat,
        includeMetadata: true,
      };

      const result = await repository.exportAuditLog(input);

      // Download the file
      window.open(result.downloadUrl, '_blank');
    } catch (err) {
      setError(err as Error);
    } finally {
      setExporting(false);
    }
  };

  /**
   * Handle view details
   */
  const handleViewDetails = (entry: RBACAuditLogEntry) => {
    setSelectedEntry(entry);
    setShowDetailModal(true);
  };

  /**
   * Format timestamp
   */
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  /**
   * Get operation color
   */
  const getOperationColor = (operation: string) => {
    if (operation.includes('CREATE')) return 'green';
    if (operation.includes('DELETE')) return 'red';
    if (operation.includes('UPDATE')) return 'blue';
    if (operation.includes('ASSIGN')) return 'cyan';
    if (operation.includes('REVOKE')) return 'orange';
    return 'gray';
  };

  /**
   * Get outcome icon
   */
  const getOutcomeIcon = (outcome: string) => {
    return outcome === 'success' ? (
      <IconCheck size={16} color="green" />
    ) : (
      <IconX size={16} color="red" />
    );
  };

  return (
    <Box>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <IconClipboardList size={24} />
            <Title order={3}>RBAC Audit Log</Title>
          </Group>

          <Group>
            <Tooltip label="Toggle Statistics">
              <ActionIcon
                variant={showStatistics ? 'filled' : 'light'}
                onClick={() => setShowStatistics(!showStatistics)}
              >
                <IconChartBar size={18} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Toggle View">
              <ActionIcon
                variant={viewMode === 'timeline' ? 'filled' : 'light'}
                onClick={() =>
                  setViewMode(viewMode === 'table' ? 'timeline' : 'table')
                }
              >
                <IconClock size={18} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Refresh">
              <ActionIcon variant="light" onClick={loadAuditLog} disabled={loading}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>

            <Badge size="lg" variant="light">
              {totalCount} entries
            </Badge>
          </Group>
        </Group>

        {/* Statistics Panel */}
        {showStatistics && statistics && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack gap="md">
              <Title order={4}>Audit Statistics</Title>

              <Grid>
                <Grid.Col span={{ base: 12, md: 3 }}>
                  <Paper p="md" withBorder>
                    <Stack gap="xs" ta="center">
                      <Text size="xl" fw={700}>
                        {statistics.totalOperations}
                      </Text>
                      <Text size="sm" c="dimmed">
                        Total Operations
                      </Text>
                    </Stack>
                  </Paper>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 3 }}>
                  <Paper p="md" withBorder>
                    <Stack gap="xs" ta="center">
                      <RingProgress
                        size={80}
                        thickness={8}
                        sections={[
                          {
                            value: statistics.successRate * 100,
                            color: 'green',
                          },
                        ]}
                        label={
                          <Text size="xs" ta="center" fw={700}>
                            {(statistics.successRate * 100).toFixed(1)}%
                          </Text>
                        }
                      />
                      <Text size="sm" c="dimmed">
                        Success Rate
                      </Text>
                    </Stack>
                  </Paper>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Paper p="md" withBorder>
                    <Text size="sm" fw={500} mb="xs">
                      Operations by Type
                    </Text>
                    <Stack gap="xs">
                      {statistics.operationsByType.slice(0, 5).map((op) => (
                        <Group key={op.operation} justify="space-between">
                          <Badge
                            color={getOperationColor(op.operation)}
                            variant="light"
                          >
                            {op.operation}
                          </Badge>
                          <Text size="sm">{op.count}</Text>
                        </Group>
                      ))}
                    </Stack>
                  </Paper>
                </Grid.Col>
              </Grid>

              {/* User Activity */}
              {statistics.userActivity.length > 0 && (
                <Box>
                  <Text size="sm" fw={500} mb="xs">
                    Most Active Users
                  </Text>
                  <Grid>
                    {statistics.userActivity.slice(0, 3).map((user) => (
                      <Grid.Col key={user.userId} span={{ base: 12, md: 4 }}>
                        <Paper p="sm" withBorder>
                          <Text size="sm" fw={500}>
                            {user.userName}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {user.operationCount} operations
                          </Text>
                        </Paper>
                      </Grid.Col>
                    ))}
                  </Grid>
                </Box>
              )}
            </Stack>
          </Card>
        )}

        {/* Filters */}
        <Card shadow="sm" padding="md" radius="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 3 }}>
              <TextInput
                placeholder="Search audit log..."
                leftSection={<IconSearch size={16} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.currentTarget.value)}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 2 }}>
              <Select
                placeholder="Operation"
                data={repository.getOperationTypes().map((op) => ({
                  value: op,
                  label: op.replace(/_/g, ' '),
                }))}
                value={filterOperation}
                onChange={setFilterOperation}
                clearable
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 2 }}>
              <Select
                placeholder="Entity Type"
                data={repository.getEntityTypes().map((type) => ({
                  value: type,
                  label: type.replace(/_/g, ' '),
                }))}
                value={filterEntityType}
                onChange={setFilterEntityType}
                clearable
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 2 }}>
              <Select
                placeholder="Outcome"
                data={[
                  { value: 'success', label: 'Success' },
                  { value: 'failure', label: 'Failure' },
                ]}
                value={filterOutcome}
                onChange={setFilterOutcome}
                clearable
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 3 }}>
              <Group gap="xs">
                <DatePickerInput
                  type="range"
                  placeholder="Date range"
                  value={dateRange}
                  onChange={setDateRange}
                  clearable
                  style={{ flex: 1 }}
                />
              </Group>
            </Grid.Col>
          </Grid>

          <Group justify="flex-end" mt="md" gap="xs">
            <Select
              placeholder="Export as..."
              data={[
                { value: 'CSV', label: 'CSV' },
                { value: 'JSON', label: 'JSON' },
                { value: 'PDF', label: 'PDF' },
              ]}
              value={exportFormat}
              onChange={(value) => setExportFormat(value as 'CSV' | 'JSON' | 'PDF')}
              style={{ width: 150 }}
            />
            <Button
              leftSection={<IconFileDownload size={16} />}
              onClick={handleExport}
              loading={exporting}
              variant="light"
            >
              Export
            </Button>
          </Group>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            onClose={() => setError(null)}
            withCloseButton
          >
            {error.message}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Center p="xl">
            <Loader size="lg" />
          </Center>
        )}

        {/* Table View */}
        {!loading && viewMode === 'table' && (
          <ScrollArea>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Timestamp</Table.Th>
                  <Table.Th>Operation</Table.Th>
                  <Table.Th>Actor</Table.Th>
                  <Table.Th>Entity</Table.Th>
                  <Table.Th>Outcome</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {auditLog.map((entry) => (
                  <Table.Tr key={entry.auditId}>
                    <Table.Td>
                      <Text size="sm">{formatTimestamp(entry.timestamp)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={getOperationColor(entry.operation)}
                        variant="light"
                      >
                        {entry.operation.replace(/_/g, ' ')}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="sm">{entry.actor}</Text>
                        <Text size="xs" c="dimmed">
                          {entry.actorRole}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="sm">{entry.entityName || entry.entityId}</Text>
                        <Text size="xs" c="dimmed">
                          {entry.entityType.replace(/_/g, ' ')}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>{getOutcomeIcon(entry.outcome)}</Table.Td>
                    <Table.Td>
                      <Tooltip label="View Details">
                        <ActionIcon
                          variant="light"
                          onClick={() => handleViewDetails(entry)}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {auditLog.length === 0 && (
              <Paper p="xl" radius="md" withBorder>
                <Stack ta="center" gap="md">
                  <IconClipboardList size={48} style={{ opacity: 0.3 }} />
                  <Text size="lg" c="dimmed">
                    No audit entries found
                  </Text>
                  <Text size="sm" c="dimmed">
                    Try adjusting your search or filters
                  </Text>
                </Stack>
              </Paper>
            )}
          </ScrollArea>
        )}

        {/* Timeline View */}
        {!loading && viewMode === 'timeline' && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Timeline active={auditLog.length} lineWidth={2}>
              {auditLog.map((entry) => (
                <Timeline.Item
                  key={entry.auditId}
                  bullet={getOutcomeIcon(entry.outcome)}
                  title={
                    <Group gap="xs">
                      <Badge
                        color={getOperationColor(entry.operation)}
                        variant="light"
                      >
                        {entry.operation.replace(/_/g, ' ')}
                      </Badge>
                      <Text size="sm">{entry.actor}</Text>
                    </Group>
                  }
                >
                  <Stack gap="xs">
                    <Text size="sm" c="dimmed">
                      {formatTimestamp(entry.timestamp)}
                    </Text>
                    <Text size="sm">
                      {entry.entityType.replace(/_/g, ' ')}: {entry.entityName || entry.entityId}
                    </Text>
                    {entry.errorMessage && (
                      <Alert color="red" icon={<IconAlertCircle size={16} />}>
                        {entry.errorMessage}
                      </Alert>
                    )}
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconEye size={14} />}
                      onClick={() => handleViewDetails(entry)}
                    >
                      View Details
                    </Button>
                  </Stack>
                </Timeline.Item>
              ))}
            </Timeline>

            {auditLog.length === 0 && (
              <Paper p="xl" radius="md" withBorder>
                <Stack ta="center" gap="md">
                  <IconClock size={48} style={{ opacity: 0.3 }} />
                  <Text size="lg" c="dimmed">
                    No timeline events found
                  </Text>
                </Stack>
              </Paper>
            )}
          </Card>
        )}

        {/* Pagination */}
        {totalCount > pageSize && (
          <Group justify="center">
            <Button
              variant="light"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Text size="sm">
              Page {page} of {Math.ceil(totalCount / pageSize)}
            </Text>
            <Button
              variant="light"
              onClick={() => setPage(page + 1)}
              disabled={page >= Math.ceil(totalCount / pageSize)}
            >
              Next
            </Button>
          </Group>
        )}
      </Stack>

      {/* Detail Modal */}
      <Modal
        opened={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Audit Entry Details"
        size="xl"
      >
        {selectedEntry && (
          <Stack gap="md">
            <Grid>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">
                  Audit ID
                </Text>
                <Code>{selectedEntry.auditId}</Code>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">
                  Timestamp
                </Text>
                <Text size="sm">{formatTimestamp(selectedEntry.timestamp)}</Text>
              </Grid.Col>
            </Grid>

            <Divider />

            <Grid>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">
                  Operation
                </Text>
                <Badge color={getOperationColor(selectedEntry.operation)}>
                  {selectedEntry.operation}
                </Badge>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">
                  Outcome
                </Text>
                <Group gap="xs">
                  {getOutcomeIcon(selectedEntry.outcome)}
                  <Text size="sm">{selectedEntry.outcome}</Text>
                </Group>
              </Grid.Col>
            </Grid>

            <Divider />

            <Box>
              <Text size="sm" c="dimmed" mb="xs">
                Actor
              </Text>
              <Stack gap="xs">
                <Text size="sm">{selectedEntry.actor}</Text>
                <Text size="xs" c="dimmed">
                  Role: {selectedEntry.actorRole}
                </Text>
                <Text size="xs" c="dimmed">
                  IP: {selectedEntry.ipAddress}
                </Text>
                {selectedEntry.userAgent && (
                  <Text size="xs" c="dimmed">
                    User Agent: {selectedEntry.userAgent}
                  </Text>
                )}
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Text size="sm" c="dimmed" mb="xs">
                Entity
              </Text>
              <Stack gap="xs">
                <Text size="sm">
                  Type: {selectedEntry.entityType.replace(/_/g, ' ')}
                </Text>
                <Text size="sm">ID: {selectedEntry.entityId}</Text>
                {selectedEntry.entityName && (
                  <Text size="sm">Name: {selectedEntry.entityName}</Text>
                )}
              </Stack>
            </Box>

            {(selectedEntry.beforeValue || selectedEntry.afterValue) && (
              <>
                <Divider />
                <Grid>
                  {selectedEntry.beforeValue && (
                    <Grid.Col span={6}>
                      <Text size="sm" c="dimmed" mb="xs">
                        Before
                      </Text>
                      <Code block>
                        {JSON.stringify(
                          repository.parseValue(selectedEntry.beforeValue),
                          null,
                          2
                        )}
                      </Code>
                    </Grid.Col>
                  )}
                  {selectedEntry.afterValue && (
                    <Grid.Col span={6}>
                      <Text size="sm" c="dimmed" mb="xs">
                        After
                      </Text>
                      <Code block>
                        {JSON.stringify(
                          repository.parseValue(selectedEntry.afterValue),
                          null,
                          2
                        )}
                      </Code>
                    </Grid.Col>
                  )}
                </Grid>
              </>
            )}

            {selectedEntry.errorMessage && (
              <>
                <Divider />
                <Alert color="red" icon={<IconAlertCircle size={16} />}>
                  {selectedEntry.errorMessage}
                </Alert>
              </>
            )}

            {selectedEntry.metadata && (
              <>
                <Divider />
                <Box>
                  <Text size="sm" c="dimmed" mb="xs">
                    Metadata
                  </Text>
                  <Code block>
                    {JSON.stringify(
                      repository.parseMetadata(selectedEntry.metadata),
                      null,
                      2
                    )}
                  </Code>
                </Box>
              </>
            )}
          </Stack>
        )}
      </Modal>
    </Box>
  );
}
